<script setup lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import { useFormat } from '../../composables/format';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

type RegionalEmailConfig = {
  [key: string]: { email: string; subject: string };
};

interface Props {
  balance?: number;
  overdue?: number;
  currencyCode?: string;
}

const props = withDefaults(defineProps<Props>(), {
  balance: undefined,
  overdue: undefined,
  currencyCode: undefined,
});

const { formatCurrency } = useFormat();

const balanceDisplay = () => {
  if (!props.currencyCode) {
    return '';
  }

  return formatCurrency(props.currencyCode, props.balance || 0, false);
};

const overdueDisplay = () => {
  if (!props.currencyCode) {
    return '';
  }

  return formatCurrency(props.currencyCode, props.overdue || 0, false);
};

const isValidBalance = () => {
  return !isNaN(props.balance || 0) && props.currencyCode?.length !== 0;
};

const statementRequestEmail = (region: string) => {
  const emailConfig: RegionalEmailConfig = {
    eu: {
      email: t('viewAccounting.EUStatementRequestEmail'),
      subject: t('viewAccounting.EUStatementRequestSubject'),
    },
    'us-ca': {
      email: t('viewAccounting.USAndCanadaStatementRequestEmail'),
      subject: t('viewAccounting.USAndCanadaStatementRequestSubject'),
    },
  };
  let emailIdAndSubject = emailConfig[region] || emailConfig['us-ca'];

  return `mailto:${emailIdAndSubject.email}?subject=${emailIdAndSubject.subject}`;
};
</script>

<template>
  <div class="paper-container" data-cy="accounting-container">
    <div class="balance-container">
      <div class="text" data-cy="account-statement-text">
        <strong class="question">{{ $t('viewAccounting.accountBalanceQuestion') }}</strong>
        <div>
          {{ $t('viewAccounting.EURequest') }}
          <a target="_system" :href="statementRequestEmail('eu')">{{ $t('viewAccounting.here') }}</a>
        </div>
        <div>
          {{ $t('viewAccounting.USAndCanadaRequest') }}
          <a target="_system" :href="statementRequestEmail('us-ca')">{{ $t('viewAccounting.here') }}</a>
        </div>
      </div>
      <div class="amounts-container" data-cy="amounts-container">
        <div v-if="isValidBalance()" class="account-balance">
          <div class="amount-box balance" data-cy="account-balance-box">
            <div class="amount-title" data-cy="account-balance-title">
              <label class="balance-label">{{ $t('viewAccounting.account_balance') }}</label>
              <span class="has-tooltip" data-cy="account-balance-tooltip">
                <BackstageIcon name="black-info" />
                <span class="tooltip"> {{ $t('viewAccounting.account_balance_tootltip') }} </span>
              </span>
            </div>
            <span class="balance-amount" data-cy="account-balance-amount">{{ balanceDisplay() }}</span>
          </div>
          <div class="amount-box overdue" data-cy="balance-overdue-box">
            <div class="amount-title" data-cy="balance-overdue-title">
              <label class="balance-label"> {{ $t('viewAccounting.balance_overdue') }}</label>
              <span class="has-tooltip" data-cy="balance-overdue-tooltip">
                <BackstageIcon name="black-info" />
                <span class="tooltip">
                  {{ $t('viewAccounting.balance_overdue_tootltip') }}
                </span>
              </span>
            </div>
            <span class="balance-amount" data-cy="balance-overdue-amount">{{ overdueDisplay() }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.paper-container {
  margin-bottom: 2.25rem;
  padding: 1.625rem 1.25rem;
  width: 100%;
  background-color: var(--primary-bg-color);

  .balance-container {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 20px;

    .text {
      text-align: left;
      display: flex;
      flex-direction: column;
      font-size: 0.875rem;

      .question {
        padding-bottom: 0.25rem;
        font-size: 1rem;
      }

      a {
        text-decoration: underline;
      }
    }

    .account-balance {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 20px;

      .amount-box {
        text-align: left;
        background-color: var(--bg-page-color);
        padding: 0.75rem;
        min-width: 230px;

        .balance-label {
          font-size: 0.875rem;
        }

        &.balance {
          .balance-amount {
            color: var(--primary-text-color);
          }
        }

        &.overdue {
          .balance-amount {
            color: var(--scarlet-red-color);
          }

          border-bottom: var(--scarlet-red-color) solid 1px;
        }

        .balance-amount {
          display: block;
          font-size: 1rem;
          padding: 2px 0;
        }
      }

      &.hidden {
        visibility: hidden;
      }
    }
  }

  .amount-title {
    display: flex;
    align-items: center;
    gap: 4px;

    .has-tooltip {
      @include breakpoint(medium) {
        display: block;
        margin: 5px 0 0;
        padding: 0;
        background-color: unset;
      }

      &:hover {
        .tooltip {
          display: block;
        }
      }

      position: relative;
      cursor: help;

      .tooltip {
        text-align: initial;
        color: var(--primary-text-color);
        position: absolute;
        bottom: 170%;
        right: 50%;
        width: max-content;
        min-width: 15em;
        max-width: 17em;
        z-index: 1;
        display: none;
        height: auto;
        transform: translateX(50%);
        padding: 18px 24px;
        box-shadow: 0 20px 40px -14px rgb(0 0 0 / 25%);
        background-position: center;
        background-size: contain;
        background-repeat: no-repeat;
        background-color: var(--primary-bg-color);
      }
    }
  }
}
</style>
